import { useGetMyProfileQuery } from '../../../../services/ProfileService';
import { useGetRefundWorklistQuery, useGetRefundApprovalsByUserQuery, REFUND_WORKLIST_TYPES } from '../../../../services/RefundService';
import { RefundStatusSummary } from './RefundDashboardCard';
import { STATUS_CONFIGS } from './dashboardConstants';

// Custom hook to aggregate refund data
export const useRefundDashboardData = (company: string, center: string) => {
  const shouldSkip = !company || !center;
  const { data: profileData } = useGetMyProfileQuery();
  const userName = profileData?.upn || '';
  
  // Fetch data for all refund types
  const startedQuery = useGetRefundWorklistQuery(
    { type: 'Started', center, company },
    { skip: shouldSkip }
  );
  const inApprovalQuery = useGetRefundWorklistQuery(
    { type: 'InApproval', center, company },
    { skip: shouldSkip }
  );
  const myApprovalsQuery = useGetRefundApprovalsByUserQuery(
    { company, center, userName },
    { skip: shouldSkip || !userName }
  );
  const payableQuery = useGetRefundWorklistQuery(
    { type: 'Payable', center, company },
    { skip: shouldSkip }
  );
  const creditCardQuery = useGetRefundWorklistQuery(
    { type: 'CreditCard', center, company },
    { skip: shouldSkip }
  );
  const postableQuery = useGetRefundWorklistQuery(
    { type: 'Postable', center, company },
    { skip: shouldSkip }
  );
  const rejectedQuery = useGetRefundWorklistQuery(
    { type: 'Rejected', center, company },
    { skip: shouldSkip }
  );

  const queries = {
    Started: startedQuery,
    InApproval: inApprovalQuery,
    MyApprovals: myApprovalsQuery,
    Payable: payableQuery,
    CreditCard: creditCardQuery,
    Postable: postableQuery,
    Rejected: rejectedQuery
  };

  // Calculate summary data
  const summaries: RefundStatusSummary[] = REFUND_WORKLIST_TYPES.map(type => {
    const query = queries[type];
    const data = query.data || [];
    const count = data.length;
    
    // Handle different data structures for MyApprovals vs other types
    let totalAmount = 0;
    if (type === 'MyApprovals') {
      // RefundView[] has amount in refundDetail.amount
      totalAmount = (data as any[]).reduce((sum: number, refund: any) => 
        sum + (refund.refundDetail?.amount || 0), 0);
    } else {
      // RefundWorklistView[] has amount directly
      totalAmount = (data as any[]).reduce((sum: number, refund: any) => 
        sum + (refund.amount || 0), 0);
    }
    
    const config = STATUS_CONFIGS[type];

    return {
      type,
      count,
      totalAmount,
      icon: config.icon,
      color: config.color,
      bgColor: config.bgColor,
      displayName: config.displayName,
      description: config.description
    };
  });

  const isLoading = Object.values(queries).some(query => query.isLoading);
  const hasError = Object.values(queries).some(query => query.error);

  // Calculate totals
  const totalCount = summaries.reduce((sum, summary) => sum + summary.count, 0);
  const totalAmount = summaries.reduce((sum, summary) => sum + summary.totalAmount, 0);

  return {
    summaries,
    isLoading,
    hasError,
    totalCount,
    totalAmount
  };
};